import React from 'react';
import PageHero from '../components/PageHero';
import { CONTACT_INFO } from '../constants';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import RevealOnScroll from '../components/RevealOnScroll';

const ContactPage: React.FC = () => {
  return (
    <>
      <PageHero 
        title="Get In Touch"
        subtitle="We're here to help you with your shipping needs."
      />
      
      <section className="py-12 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid lg:grid-cols-2 gap-8 md:gap-16 mb-12 md:mb-20">
                  
                  {/* Contact Info */}
                  <RevealOnScroll>
                      <h2 className="text-2xl font-bold text-slate-900 mb-6 md:mb-8">Contact Information</h2>
                      <div className="space-y-6 md:space-y-8">
                          <div className="flex items-start gap-4 p-5 md:p-6 bg-slate-50 rounded-2xl">
                              <div className="bg-brand-100 p-3 rounded-full text-brand-600 shrink-0">
                                  <MapPin className="w-6 h-6" />
                              </div>
                              <div>
                                  <h3 className="font-bold text-slate-900">Colombo Warehouse</h3>
                                  <p className="text-slate-600 mt-1 text-sm md:text-base">{CONTACT_INFO.address}</p>
                              </div>
                          </div>
                          
                          <div className="flex items-start gap-4 p-5 md:p-6 bg-slate-50 rounded-2xl">
                              <div className="bg-brand-100 p-3 rounded-full text-brand-600 shrink-0">
                                  <Phone className="w-6 h-6" />
                              </div>
                              <div>
                                  <h3 className="font-bold text-slate-900">Phone Support</h3>
                                  <p className="text-slate-600 mt-1 text-sm md:text-base">Sri Lanka: <a href={`tel:${CONTACT_INFO.phoneSriLanka}`} className="hover:text-brand-600">{CONTACT_INFO.phoneSriLanka}</a></p>
                                  <p className="text-slate-600 text-sm md:text-base">Maldives: <a href={`tel:${CONTACT_INFO.phoneMaldives}`} className="hover:text-brand-600">{CONTACT_INFO.phoneMaldives}</a></p>
                              </div>
                          </div>

                          <div className="flex items-start gap-4 p-5 md:p-6 bg-slate-50 rounded-2xl">
                              <div className="bg-brand-100 p-3 rounded-full text-brand-600 shrink-0">
                                  <Mail className="w-6 h-6" />
                              </div>
                              <div>
                                  <h3 className="font-bold text-slate-900">Email</h3>
                                  <p className="text-slate-600 mt-1 text-sm md:text-base"><a href={`mailto:${CONTACT_INFO.email}`} className="hover:text-brand-600">{CONTACT_INFO.email}</a></p>
                              </div>
                          </div>
                      </div>
                  </RevealOnScroll>

                  {/* Form */}
                  <RevealOnScroll delay={200}>
                      <div className="bg-white rounded-3xl shadow-lg border border-slate-100 p-6 md:p-8 mt-8 lg:mt-0">
                          <h2 className="text-2xl font-bold text-slate-900 mb-6">Send us a Message</h2>
                          <form className="space-y-4 md:space-y-6">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                                  <div>
                                      <label className="block text-sm font-medium text-slate-700 mb-2">Name</label>
                                      <input type="text" className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none transition text-base" placeholder="Your Name" />
                                  </div>
                                  <div>
                                      <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                                      <input type="email" className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none transition text-base" placeholder="your@email.com" />
                                  </div>
                              </div>
                              <div>
                                  <label className="block text-sm font-medium text-slate-700 mb-2">Subject</label>
                                  <input type="text" className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none transition text-base" placeholder="Shipping Inquiry" />
                              </div>
                              <div>
                                  <label className="block text-sm font-medium text-slate-700 mb-2">Message</label>
                                  <textarea rows={4} className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none transition text-base" placeholder="How can we help you?"></textarea>
                              </div>
                              <button type="submit" className="w-full bg-brand-600 hover:bg-brand-700 text-white font-bold py-3.5 md:py-4 rounded-xl shadow-lg flex items-center justify-center gap-2 transition transform active:scale-95 text-base md:text-lg">
                                  <Send className="w-5 h-5" /> Send Message
                              </button>
                          </form>
                      </div>
                  </RevealOnScroll>
              </div>

              {/* Map Section */}
              <RevealOnScroll delay={300}>
                  <div className="rounded-3xl overflow-hidden shadow-lg border border-slate-100 h-[300px] md:h-[400px] w-full bg-slate-100 relative">
                     <iframe 
                        width="100%" 
                        height="100%" 
                        style={{ border: 0 }}
                        loading="lazy" 
                        allowFullScreen
                        referrerPolicy="no-referrer-when-downgrade"
                        title="OTL Express Colombo Location"
                        src="https://maps.google.com/maps?q=13%20Lauries%20Road%2C%20Colombo%2004%2C%20Sri%20Lanka&t=&z=15&ie=UTF8&iwloc=&output=embed"
                      >
                     </iframe>
                     <div className="absolute bottom-4 left-4 bg-white p-4 rounded-xl shadow-lg max-w-xs hidden sm:block">
                         <p className="font-bold text-slate-900">OTL Express Warehouse</p>
                         <p className="text-xs text-slate-500">13 Lauries Rd, Colombo 04</p>
                     </div>
                  </div>
              </RevealOnScroll>
          </div>
      </section>
    </>
  );
};

export default ContactPage;